import * as React from "react";
import cx from "classnames";
import { type VariantProps, cva } from "class-variance-authority";
import { Typography } from "@picklebet/ui";
import { Separator } from "@picklebet/ui";
import { RequiresAuth } from "@/features/auth/components/RequiresAuth";

import * as styles from "./Page.module.scss";

const pageVariants = cva(null, {
  variants: {
    variant: {
      // using CVA because there will be more variants later
      centered: styles.centered,
      full: styles.full,
    },
  },
  defaultVariants: {
    variant: "centered",
  },
});

type PageProps = VariantProps<typeof pageVariants> & {
  className?: string;
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  showSeparator?: boolean;
  children?: React.ReactNode;
  requiresAuth?: boolean;
  noSpacing?: boolean;
  noSpacingTitleTop?: boolean;
  noSpacingTitleBottom?: boolean;
  banner?: React.ReactNode;
  hideTitleOnMobile?: boolean;
};

type TitleProps = {
  title: React.ReactNode;
  shouldAddSpacingTopOnMobile?: boolean;
  shouldAddSpacingBottom?: boolean;
  hideTitleOnMobile: boolean;
};

const Title = ({
  title,
  hideTitleOnMobile,
  shouldAddSpacingTopOnMobile = true,
  shouldAddSpacingBottom = true,
}: TitleProps) => {
  if (typeof title === "string") {
    return (
      <Typography
        variant={`h2`}
        as={`h1`}
        noSpacing
        className={cx({
          [styles.withSpacingTopOnMobile]: shouldAddSpacingTopOnMobile,
          [styles.withSpacingBottom]: shouldAddSpacingBottom,
          [styles.hideOnMobile]: hideTitleOnMobile,
        })}
      >
        {title}
      </Typography>
    );
  }

  return title;
};

const Page = ({
  showSeparator = true,
  title,
  subTitle,
  children,
  className,
  variant,
  requiresAuth = false,
  noSpacing = false,
  noSpacingTitleTop = false,
  noSpacingTitleBottom = false,
  banner,
  hideTitleOnMobile = false,
}: PageProps) => {
  const Wrapper = requiresAuth ? RequiresAuth : React.Fragment;
  // for title
  const shouldAddSpacingTopOnMobile = !noSpacing && !noSpacingTitleTop;
  const shouldAddSpacingBottom = !noSpacing && !noSpacingTitleBottom;

  return (
    <>
      <Wrapper>
        <div className={cx(styles.page, pageVariants({ variant }), className)}>
          {title && (
            <Title
              title={title}
              shouldAddSpacingTopOnMobile={shouldAddSpacingTopOnMobile}
              shouldAddSpacingBottom={shouldAddSpacingBottom}
              hideTitleOnMobile={hideTitleOnMobile}
            />
          )}
          {subTitle && (
            <Typography variant={`body`} className={styles.subTitle} noSpacing>
              {subTitle}
            </Typography>
          )}
          {banner}
          {showSeparator && <Separator />}
          {children}
        </div>
      </Wrapper>
    </>
  );
};

const PageContent = ({
  children,
  noSpacing,
  className,
}: {
  children: React.ReactNode;
  noSpacing?: boolean;
  className?: string;
}) => {
  return (
    <div
      className={cx(
        styles.pageContent,
        {
          [styles.noSpacing]: noSpacing,
        },
        className,
      )}
    >
      {children}
    </div>
  );
};

export { Page, PageContent };
